import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { isAndroid, isIOS, isNative } from './platform.js';

/**
 * System chrome: status bar, splash screen and software keyboard.
 *
 * These are the details that separate "a website in a box" from an app, and
 * they are the first things a store reviewer notices. All of it no-ops on web.
 */

const THEME_COLOR = '#0a0a0a';

async function configureStatusBar() {
  if (!isNative) return;
  try {
    // The app is dark-only, so the status bar needs light glyphs.
    await StatusBar.setStyle({ style: Style.Dark });
    if (isAndroid) {
      await StatusBar.setBackgroundColor({ color: THEME_COLOR });
    }
    // Draw behind the status bar; src/styles/native.css pads content clear of
    // it using the safe-area insets.
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (err) {
    console.warn('[ironpact] status bar setup failed', err);
  }
}

async function configureKeyboard() {
  if (!isNative) return;
  try {
    // Resize the web view rather than the body: the fixed bottom nav should
    // travel with the keyboard instead of being covered by it.
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    if (isIOS) await Keyboard.setAccessoryBarVisible({ isVisible: true });
  } catch (err) {
    console.warn('[ironpact] keyboard setup failed', err);
  }

  // Hide the bottom nav while typing — on a short screen it otherwise sits on
  // top of the field being edited.
  Keyboard.addListener('keyboardWillShow', () => {
    document.body.classList.add('keyboard-open');
  });
  Keyboard.addListener('keyboardWillHide', () => {
    document.body.classList.remove('keyboard-open');
  });
}

/**
 * Dismisses the splash screen once the shell has actually painted, rather than
 * on a fixed timer. `autoHide` is off in capacitor.config.json so a slow first
 * load shows the splash instead of a blank web view.
 */
async function hideSplash() {
  if (!isNative) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch (err) {
    console.warn('[ironpact] splash hide failed', err);
  }
}

export async function installChrome() {
  await Promise.all([configureStatusBar(), configureKeyboard()]);

  // Two frames after the module layer runs the shell has laid out; waiting for
  // `load` would keep the splash up through font and image fetches.
  requestAnimationFrame(() => requestAnimationFrame(hideSplash));
}
